// Menus and picker dialogs are portalled to the body but still bubble focus
// into the composer through the React tree. The compact composer mounts a
// different control set per state, so letting one of these surfaces flip
// expanded unmounts the control that owns it — destroying the picker the same
// tick it opens.
const COMPOSER_PORTAL_SURFACE_SELECTOR =
  '[data-radix-popper-content-wrapper], [role="dialog"], [role="alertdialog"]';

const DISMISS_SURFACE_SELECTOR = '[data-compact-composer-dismiss]';
const INTERACTION_SURFACE_SELECTOR = '[data-compact-composer-interaction]';
const CONTROL_SELECTOR = '[data-compact-composer-control]';

export const pointerDownDismisses = (
  target: Element | null,
  insideContainer: boolean
): boolean => {
  if (insideContainer) return false;
  // An open alert dialog (Approve for me) leaves nothing behind it clickable,
  // and a press on its backdrop does not close it, so no press is a click away.
  if (document.querySelector('[role="alertdialog"]')) return false;
  // An interaction surface dismissing itself ends the engagement it was
  // holding open, so the composer settles back with it.
  if (target?.closest(DISMISS_SURFACE_SELECTOR)) return true;
  if (target?.closest(INTERACTION_SURFACE_SELECTOR)) return false;
  if (target?.closest(COMPOSER_PORTAL_SURFACE_SELECTOR)) return false;
  return true;
};

export const cardFocusEngages = (target: Element | null): boolean => {
  if (target?.closest(CONTROL_SELECTOR)) return false;
  // A control's menu is portalled out of that wrapper but still bubbles focus
  // here through the React tree, so browsing it is not engaging the composer.
  if (target?.closest(COMPOSER_PORTAL_SURFACE_SELECTOR)) return false;
  return true;
};

export const cardBlurDisengages = (
  nextFocused: EventTarget | null,
  insideCard: boolean
): boolean => {
  // Layout / motion remounts fire blur with no related target and would
  // otherwise collapse the compact composer, remount the idle surface, and
  // immediately refocus — a setState loop.
  if (nextFocused == null) return false;
  // Safari / WKWebView often reports body or <html> as relatedTarget when a
  // contenteditable blurs before a toolbar control receives focus. Treating
  // those like a null relatedTarget keeps engagement up; real outside dismiss
  // still goes through the document pointerdown listener.
  if (nextFocused === document.body || nextFocused === document.documentElement) {
    return false;
  }
  if (insideCard) return false;
  if (nextFocused instanceof Element && nextFocused.closest(INTERACTION_SURFACE_SELECTOR))
    return false;
  if (
    nextFocused instanceof Element &&
    nextFocused.closest(COMPOSER_PORTAL_SURFACE_SELECTOR)
  )
    return false;
  return true;
};
